import { ErrorFallbackError } from "@/ErrorHandling/types";
import { parseError } from "@/ErrorHandling/utils";
import { nanoid } from "nanoid";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useTranslation } from "react-i18next";

export function useParsedError(error: ErrorFallbackError | undefined): string | undefined {
  const { t } = useTranslation();
  if (!error) {
    return undefined;
  }
  return parseError(error, t).msg;
}

export function useErrorNotification(): { show: (error: ErrorFallbackError) => void; close: () => void } {
  const { t } = useTranslation();
  // Note this key is unique for each render
  // this is required to avoid duplicate child keys for when multiple errors are rendered
  const key = `error_` + nanoid();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const fb = React.useCallback(
    (error: ErrorFallbackError) => {
      const { msg } = parseError(error, t);
      enqueueSnackbar({
        key: key,
        persist: false,
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "right",
        },
        message: msg,
      });
    },
    [key],
  );
  return { show: fb, close: () => closeSnackbar(key) };
}

export function useDisplayErrorNotification(error: ErrorFallbackError | undefined) {
  const notification = useErrorNotification();
  React.useEffect(() => {
    if (error) {
      notification.show(error);
    } else {
      notification.close();
    }
    return () => notification.close();
  }, [error]);
}
